/**
 * Locale-aware formatting for the Promo workspace. Locale follows the active i18next language.
 * A local copy of `features/catalog/lib/format.ts` — FSD forbids importing another feature.
 */
import { getIntlLocale } from '@/shared/i18n';

/** Format a money amount in RUB, or an em dash when absent. */
export function formatPrice(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(getIntlLocale(), {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(value);
}

/** Format a plain count (thousands separated). */
export function formatNumber(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(getIntlLocale()).format(value);
}

/** Format an ISO timestamp, or an em dash when absent/invalid. */
export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/** Format an ISO date (no time), or an em dash when absent/invalid. */
export function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(getIntlLocale(), { dateStyle: 'medium' }).format(date);
}

/** Format a percentage value (already 0-100 scale), e.g. `15` -> "15%". */
export function formatPercent(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${new Intl.NumberFormat(getIntlLocale(), { maximumFractionDigits: 1 }).format(value)}%`;
}
